import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Control } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { useDebounceValue } from 'usehooks-ts';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { Competency } from '~/shared/data-contract/job-profile';
import { getCompetencyTitle } from '~/shared/data-contract/job-profile';
import { DropdownMenuItem } from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconCircleCheckLight, IconSearch } from '~/ui/core/icons';
import { Input } from '~/ui/core/input/Input';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { Spinner } from '~/ui/core/spinner/Spinner';

import { useCompetencies } from '../../api/use-competencies';
import { useTrackSearch } from '../../hooks/analytics/use-track-search';
import type { ProfileForm } from '../types';

type UseCompetencySearchParams = {
    readonly architectureId?: string;
    readonly control: Control<ProfileForm>;
    readonly debounceDelay: number;
    readonly onCompetencyChange?: () => void;
};

type UseCompetencySearchResult = {
    readonly displayedCompetencies: readonly Competency[];
    readonly handleCompetencySelect: (competencyId: string) => void;
    readonly isCompetencySelected: (competencyId: string) => boolean;
    readonly isLoading: boolean;
    readonly searchTerm: string;
    readonly setSearchTerm: (value: string) => void;
};

const useCompetencySearch = ({
    architectureId,
    control,
    debounceDelay,
    onCompetencyChange,
}: UseCompetencySearchParams): UseCompetencySearchResult => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm] = useDebounceValue(searchTerm, debounceDelay, { leading: true });
    const { applicationState } = useApplicationContext();
    const { data: competencies, isLoading } = useCompetencies();
    const { trackSearchExecuted } = useTrackSearch({ architectureId });

    const { fields, insert } = useFieldArray({
        control,
        keyName: '_id',
        name: 'competencies',
    });

    const competenciesArray = is.undefined(competencies) ? [] : [...competencies.values()];

    const displayedCompetencies = competenciesArray.filter((competency) => {
        const title = getCompetencyTitle(competency, applicationState.language.code);

        return title.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const debouncedResultCount = competenciesArray.filter((competency) => {
        const title = getCompetencyTitle(competency, applicationState.language.code);

        return title.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
    }).length;

    useEffect(() => {
        if (!is.nonEmptyString(debouncedSearchTerm)) {
            return;
        }

        trackSearchExecuted({
            query: debouncedSearchTerm,
            resultCount: debouncedResultCount,
            scope: 'competencies',
        });
    }, [debouncedSearchTerm, debouncedResultCount, trackSearchExecuted]);

    const handleCompetencySelect = (competencyId: string): void => {
        const competency = competenciesArray.find((c) => c.id === competencyId);
        if (is.nullOrUndefined(competency)) {
            return;
        }

        const alreadyExists = fields.some((field) => field.id === competency.id);
        if (!alreadyExists) {
            insert(1, {
                ...competency,
                skills: [],
            });
            onCompetencyChange?.();
        }

        setSearchTerm('');
    };

    const isCompetencySelected = (competencyId: string): boolean => {
        return fields.some((field) => field.id === competencyId);
    };

    return {
        displayedCompetencies,
        handleCompetencySelect,
        isCompetencySelected,
        isLoading,
        searchTerm,
        setSearchTerm,
    };
};

type CompetencySearchContentProps = {
    readonly architectureId?: string;
    readonly control: Control<ProfileForm>;
    readonly debounceDelay?: number;
    readonly onCompetencyChange?: () => void;
};

const SKELETON_ITEM_COUNT = 4;

export const CompetencySearchContent: FunctionComponent<CompetencySearchContentProps> = ({
    architectureId,
    control,
    debounceDelay = 500,
    onCompetencyChange,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const translate = useTranslate();
    const { applicationState } = useApplicationContext();
    const alreadyAddedTooltip = translate('pages.job-architecture-v2.job-profile-skill-already-added-tooltip');
    const {
        displayedCompetencies,
        handleCompetencySelect,
        isCompetencySelected,
        isLoading,
        searchTerm,
        setSearchTerm,
    } = useCompetencySearch({
        architectureId,
        control,
        debounceDelay,
        onCompetencyChange,
    });
    const showSkeletons = isLoading && displayedCompetencies.length === 0;

    useEffect(() => {
        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
    }, []);

    return (
        <div className="flex flex-col gap-s min-w-[240px] overflow-hidden">
            <div className="flex items-center bg-transparent border-b border-gray-400 gap-s p-s relative">
                <label htmlFor="competency-search-input" className="flex items-center text-gray-700 opacity-50">
                    <IconSearch size={12} />
                </label>
                <Input
                    ref={inputRef}
                    className="text-gray-700 flex-1 text-body-s font-normal pl-s tracking-tight"
                    id="competency-search-input"
                    variant="ghost"
                    placeholder={translate('pages.job-architecture-v2.job-profile-competency-search-placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDownCapture={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                />
                {isLoading && <Spinner className="absolute right-m" />}
            </div>
            <ScrollArea orientation="vertical">
                <div className="flex flex-auto flex-col gap-1 h-[25vh]">
                    {showSkeletons &&
                        Array.from({ length: SKELETON_ITEM_COUNT }).map((_, index) => (
                            <SkeletonBase
                                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders are static.
                                key={index}
                                style={{ height: '24px', margin: '4px 8px', width: '60%' }}
                            />
                        ))}
                    {!showSkeletons && is.emptyArray(displayedCompetencies) && (
                        <div className="text-gray-500 text-sm p-m text-center">
                            {translate('validation.empty-search-results')}
                        </div>
                    )}
                    {!showSkeletons &&
                        displayedCompetencies.map((competency) => {
                            const title = getCompetencyTitle(competency, applicationState.language.code);

                            if (isCompetencySelected(competency.id)) {
                                return (
                                    <DropdownMenuItem
                                        key={competency.id}
                                        className="flex-1 bg-status-success-surface"
                                        disabled={true}
                                        tooltipContent={alreadyAddedTooltip}
                                    >
                                        <span className="min-w-0 truncate text-text-form">{title}</span>
                                        <span className="ml-auto flex shrink-0 [&_svg]:!text-status-success-text">
                                            <IconCircleCheckLight size={14} />
                                        </span>
                                    </DropdownMenuItem>
                                );
                            }

                            return (
                                <DropdownMenuItem
                                    key={competency.id}
                                    onSelect={() => handleCompetencySelect(competency.id)}
                                >
                                    <span className="min-w-0 truncate">{title}</span>
                                </DropdownMenuItem>
                            );
                        })}
                </div>
            </ScrollArea>
        </div>
    );
};
